// Demo-home SCENARIOS — the same Newell demo bundle reshaped into other store types, so
// Home's layout can be judged for each (?scenario=polygon on /storalytic_demo_store).
// Purely a design aid: the page labels itself as a scenario; nothing else reads this.
//
// polygon — a store whose product / checkout zones are all polygons and that has, like
// Van Wiemeersch in production on 2026-10-15:
//   · no tripwire product zone        → no "More shoppers to a zone" block
//   · no Service / Info / Pick Up      → no sub-rows under SERVE
//   · no action implemented yet        → no "Delivered by Allen", the nudge instead
//   · no own-best history above today  → ATTRACT at 100% of its best weeks
//   · fewer shoppers stopping than the sector research ceiling assumes
//                                      → best-in-class far out (> 200% of its best)
//   · a checkout baseline still building (no best-weeks wait yet)
//   · goals that don't line up with the data (one on a top-3 card with another lever,
//     one on a zone that has no focus card)

import { applyTypeChange, applyEntranceTypeChange, applySettingsChange } from '@/demo/changesTodayDemo'
// Sample text carries its Dutch alongside (withNl): read in the portal's current language.
import { withNl, copyWithNl } from '@/demo/demoLang'

const TRIPWIRE_ZONE = 'Paint & Decor'
// Share of today's stoppers kept: fewer shoppers stop than in the base demo, so the
// research ceiling sits far beyond this store's own level (as at Van Wiemeersch).
const STOPPERS_KEPT = 0.55

const withoutZone = (map, zone) => {
  if (!map || typeof map !== 'object') return map
  const { [zone]: _drop, ...rest } = map
  return rest
}
const scaled = (map, f) =>
  Object.fromEntries(Object.entries(map || {}).map(([k, v]) => [k, Math.round(Number(v || 0) * f)]))

// The weekly nudge narrowed to the one suggestion these scenarios have.
const oneNewSuggestion = (f) => copyWithNl(f, {
  title: 'Allen has 1 new suggestion for you', zones: ['Garden Utilities'],
  detail: 'New this week: Garden Utilities. It\'s waiting on the Actions tab — implement it, keep it in consideration, or tell Allen it\'s not for you.',
}, {
  title: 'Allen heeft 1 nieuw voorstel voor je',
  detail: 'Nieuw deze week: Garden Utilities. Het staat klaar in de tab Acties — voer het uit, houd het in overweging of laat Allen weten dat het niet bij je past.',
})

export function applyPolygonOnly(data) {
  const fw = data.fixedWidgetsData || {}
  const fixedWidgetsData = {
    ...fw,
    product_zone_visitors_counts: withoutZone(fw.product_zone_visitors_counts, TRIPWIRE_ZONE),
    product_zone_clear_lingerers_counts: scaled(
      withoutZone(fw.product_zone_clear_lingerers_counts, TRIPWIRE_ZONE), STOPPERS_KEPT),
    product_zone_funnels: withoutZone(fw.product_zone_funnels, TRIPWIRE_ZONE),
    product_zones_geometry: withoutZone(fw.product_zones_geometry, TRIPWIRE_ZONE),
    // Checkout baseline still building: a current wait, but no best-weeks wait yet, so
    // no reduction / rung to aim at (the shape the backend emits while calibrating).
    _service: {
      serve: {
        headlinePct: 27, peerTickPct: null, ceilingTickPct: 4,
        currentWaitSec: 95, ownBestWaitSec: null, cohortWaitSec: null, targetWaitSec: null,
        activeRung: null, activeRungKey: null, waitReductionMin: null,
        atRiskEur: null, atFloor: false,
        // backend shape: the ladder under climb, no reduction while calibrating
        climb: { activeRung: null, activeRungKey: null, waitReductionMin: null,
                 currentWaitSec: 95, atFloor: false },
      },
      waiting: [],
    },
  }
  // Polygon product + checkout zones only (entrance kept); no service-type zones.
  const SERVICE_TYPES = new Set([9, 10, 11, 15, 16])
  const zones = (data.zones || []).filter((z) =>
    !SERVICE_TYPES.has(z.DetectionZoneTypeId)
    && !(z.DetectionZoneTypeId === 6 && !(Array.isArray(z.Polygon) && z.Polygon.length >= 3)))

  const findings = (data.findings || [])
    .filter((f) => f.kind !== 'guidance_verdict'                // nothing measured yet
      && f.kind !== 'counter_long_wait')                         // no service desk here
    .map((f) => (f.kind === 'new_suggestions' ? oneNewSuggestion(f) : f))

  return {
    ...data,
    fixedWidgetsData,
    zones,
    zoneOverrides: {},                                          // no own-best above today
    recommendations: {
      ...withoutZone(data.recommendations, TRIPWIRE_ZONE),
      // With fewer shoppers stopping, Safety Shoes' lever here is engagement, not reach.
      'Safety Shoes': withNl({
        action: 'Put one boot on a low plinth at the aisle mouth with a "try it on" bench beside it, so passing shoppers have a reason to stop.',
        why: 'Shoppers reach the aisle but few stop — the range doesn\'t invite a closer look.',
      }, {
        action: 'Zet één werkschoen op een lage sokkel aan het begin van het gangpad, met een bankje ernaast om te passen, zodat voorbijlopende bezoekers een reden hebben om te blijven staan.',
        why: 'Bezoekers komen tot in het gangpad, maar weinigen blijven staan: het assortiment nodigt niet uit om beter te kijken.',
      }),
    },
    findings,
    valueDelivered: null,
    // Its own briefing text (the base demo's names a Lane-2 € at risk this store lacks).
    // No figures in it: the numbers live in the cards and rows below.
    digest: withNl({
      slot: 'past_30_days', language: 'en',
      digest: '<p>Your product zones are at their best weeks — the next step up is your peers. '
        + 'The most within reach is in <strong>Power Tools</strong> and <strong>Garden Utilities</strong>, '
        + 'both by getting more passing shoppers to stop. Your goal to lift <strong>conversion</strong> on '
        + 'Safety Shoes is worth a rethink — the data points to <strong>engagement</strong> first.</p>'
        + '<p>Checkout: Allen is still learning your best weeks, so there is no comparison yet.</p>',
    }, {
      language: 'nl',
      digest: '<p>Je productzones zitten op het niveau van je beste weken — de volgende stap is vergelijkbare winkels. '
        + 'Het meeste is te winnen in <strong>Power Tools</strong> en <strong>Garden Utilities</strong>, '
        + 'telkens door meer voorbijlopende bezoekers te laten blijven staan. Je doel voor meer <strong>conversie</strong> bij '
        + 'Safety Shoes verdient een tweede blik — de data wijst eerst naar <strong>engagement</strong>.</p>'
        + '<p>Kassa: Allen leert je beste weken nog kennen, dus er is nog geen vergelijking.</p>',
    }),
    goals: [
      // On a top-3 card, but the data points to another lever → the amber chip.
      withNl({ title: 'Lift conversion on Safety Shoes', lever: 'conversion' },
        { title: 'Meer conversie bij Safety Shoes' }),
      // A zone with no focus card → listed under "Your other goals".
      withNl({ title: 'Grow engagement in the Promoted Articles Zone', lever: 'engagement', zone: 'Promoted Articles Zone' },
        { title: 'Meer engagement in de Promoted Articles Zone' }),
    ],
  }
}

// tripwire — every zone is a counting line (like Kringwinkel West in production): it
// counts who arrives, so there is no engagement, conversion or € anywhere, and no
// checkout / service zones. What it CAN measure: footfall, and each zone's reach
// (share of shoppers who come to it) against that zone's own best weeks.
const PRODUCT_ZONES = ['Power Tools', 'Garden Utilities', 'Safety Shoes', 'Promoted Articles Zone', TRIPWIRE_ZONE]
// Each zone's reach in its best weeks + its normal week-to-week swing. Two zones sit at
// their best (no card); three can draw more shoppers.
const REACH_BEST = {
  'Garden Utilities':       { rung1Reach: 0.50, reachWobble: 0.02 },
  'Safety Shoes':           { rung1Reach: 0.12, reachWobble: 0.01 },
  [TRIPWIRE_ZONE]:          { rung1Reach: 0.19, reachWobble: 0.015 },
  'Power Tools':            { rung1Reach: 0.45, reachWobble: 0.02 },
  'Promoted Articles Zone': { rung1Reach: 0.09, reachWobble: 0.01 },
}
const REACH_RECS = {
  'Garden Utilities': withNl({
    action: 'Move one seasonal hero display to the end of the main walkway, facing the entrance, so Garden registers before shoppers turn into the store.',
    why: 'Fewer of your shoppers reach Garden than in its best weeks.',
  }, {
    action: 'Zet één opvallende seizoensdisplay aan het einde van het hoofdpad, met de voorkant naar de ingang, zodat bezoekers Garden opmerken voordat ze de winkel in lopen.',
    why: 'Minder van je bezoekers komen bij Garden dan in de beste weken van deze zone.',
  }),
  'Safety Shoes': withNl({
    action: 'Bring a low display of the full size run to the aisle edge with a clear "sizes 39–48 in stock" sign, so the category registers from the main walkway.',
    why: 'Few shoppers find this aisle — it sits off the main route.',
  }, {
    action: 'Zet een lage display met alle maten aan de rand van het gangpad, met een duidelijk bord "maten 39–48 op voorraad", zodat de categorie opvalt vanaf het hoofdpad.',
    why: 'Weinig bezoekers vinden dit gangpad: het ligt naast de hoofdroute.',
  }),
}
const DWELL_AVG_SEC = {
  'Garden Utilities': 70, [TRIPWIRE_ZONE]: 85, 'Power Tools': 95, 'Safety Shoes': 45, 'Promoted Articles Zone': 40,
}
// A dwell change (coach/findings.py::_dwell_change_findings shape) — context, not a flag.
const DWELL_CHANGE = withNl({
  id: 'n-dwell-paint', kind: 'dwell_change', severity: 'info',
  title: 'Longer visits — Paint & Decor',
  detail: 'Visitors are spending more time in Paint & Decor — about 1 min 25 sec on average, was 1 min.',
  when: 'this month vs last month',
}, {
  title: 'Langere bezoeken — Paint & Decor',
  detail: 'Bezoekers blijven langer in Paint & Decor: gemiddeld ongeveer 1 min 25 sec, eerder 1 min.',
  when: 'deze maand t.o.v. vorige maand',
})
// Findings that need zones this store doesn't have (checkout, service desk, € proof).
const NEEDS_POLYGON_OR_CHECKOUT = new Set(['guidance_verdict', 'checkout_change', 'counter_long_wait'])

export function applyTripwireOnly(data) {
  const fw = data.fixedWidgetsData || {}
  const funnels = {}
  for (const z of PRODUCT_ZONES) {
    funnels[z] = { store_visitors: 11320, zone_visitors: (fw.product_zone_visitors_counts || {})[z] || 0 }
  }
  const fixedWidgetsData = {
    ...fw,
    product_zone_clear_lingerers_counts: Object.fromEntries(PRODUCT_ZONES.map((z) => [z, 0])),
    product_zone_funnels: funnels,
    product_zones_geometry: Object.fromEntries(PRODUCT_ZONES.map((z) => [z, 'tripwire'])),
    _service: {},
    checkout_interaction_usage_pct: null, checkout_cashier_usage_pct: null,
    // Estimated dwell per counting zone (same fields production sends). Safety Shoes is
    // timed FROM the entrance line → a transit time; Promoted's estimate is low-confidence
    // → withheld on Home. Totals = avg seconds × the zone's visitors.
    product_zones_with_estimated_dwell: PRODUCT_ZONES,
    total_duration_per_product_zone: Object.fromEntries(Object.entries(DWELL_AVG_SEC).map(
      ([z, sec]) => [z, sec * ((fw.product_zone_visitors_counts || {})[z] || 0)])),
    product_zones_dwell_confidence: {
      'Garden Utilities': 'medium', [TRIPWIRE_ZONE]: 'high', 'Power Tools': 'high',
      'Safety Shoes': 'medium', 'Promoted Articles Zone': 'low',
    },
    product_zones_transit_source: { 'Safety Shoes': 'Main Entrance' },
  }
  // Entrance + product zones only, all as counting lines (no polygon).
  const zones = (data.zones || [])
    .filter((z) => z.DetectionZoneTypeId === 1 || z.DetectionZoneTypeId === 6)
    .map((z) => (z.DetectionZoneTypeId === 6 ? { ...z, Polygon: null } : z))

  const findings = [...(data.findings || []), DWELL_CHANGE]
    .filter((f) => !NEEDS_POLYGON_OR_CHECKOUT.has(f.kind))
    .map((f) => (f.kind === 'new_suggestions' ? oneNewSuggestion(f) : f))

  return {
    ...data,
    fixedWidgetsData,
    zones,
    zoneOverrides: REACH_BEST,
    recommendations: { ...REACH_RECS, [TRIPWIRE_ZONE]: (data.recommendations || {})[TRIPWIRE_ZONE] },
    findings,
    valueDelivered: null,
    idleWhen: null,
    digest: withNl({
      slot: 'past_30_days', language: 'en',
      digest: '<p>Your zones count who arrives, so Allen reads your store through <strong>footfall</strong> '
        + 'and how many shoppers each zone draws. <strong>Garden Utilities</strong> draws fewer shoppers than in '
        + 'its best weeks — the biggest gap in the store.</p>'
        + '<p>Your goal to grow <strong>engagement</strong> store-wide can\'t be measured yet: a counting line '
        + 'sees who passes, not who stops.</p>',
    }, {
      language: 'nl',
      digest: '<p>Je zones tellen wie er binnenkomt, dus Allen leest je winkel via de <strong>winkelbezoekers</strong> '
        + 'en hoeveel bezoekers elke zone trekt. <strong>Garden Utilities</strong> trekt minder bezoekers dan in '
        + 'zijn beste weken — het grootste verschil in de winkel.</p>'
        + '<p>Je doel voor meer <strong>engagement</strong> in de hele winkel is nog niet te meten: een tellijn '
        + 'ziet wie voorbijkomt, niet wie blijft staan.</p>',
    }),
    goals: [withNl({ title: 'Grow engagement store-wide', lever: 'engagement' },
      { title: 'Meer engagement in de hele winkel' })],
  }
}

export const SCENARIOS = {
  polygon: withNl({ label: 'polygon-only store', apply: applyPolygonOnly },
    { label: 'winkel zonder tellijnen' }),
  tripwire: withNl({ label: 'tripwire-only store', apply: applyTripwireOnly },
    { label: 'winkel met alleen tellijnen' }),
  // Zone identity phase 6.2: a zone's type changed today (demo/changesTodayDemo.js).
  type_change: withNl({ label: 'Garden Utilities changed from Product to Display Zone today', apply: applyTypeChange },
    { label: 'Garden Utilities vandaag gewijzigd van Productzone naar Displayzone' }),
  type_change_entrance: withNl({ label: 'Main Entrance changed from Entrance to Combined Entrance/Exit Zone today', apply: applyEntranceTypeChange },
    { label: 'Main Entrance vandaag gewijzigd van Ingang naar In- en uitgang' }),
  money_change: withNl({ label: 'money settings changed today', apply: applySettingsChange('money_change') },
    { label: 'omzetinstellingen vandaag gewijzigd' }),
  opening_hours_change: withNl({ label: 'opening hours changed today', apply: applySettingsChange('opening_hours_change') },
    { label: 'openingstijden vandaag gewijzigd' }),
  threshold_change: withNl({ label: 'Customer Service Desk alert threshold changed today', apply: applySettingsChange('threshold_change') },
    { label: 'alarmgrens van Customer Service Desk vandaag gewijzigd' }),
}
